import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Grid, TextField, Button, Typography, Alert, InputAdornment, IconButton,
  CircularProgress, Avatar
} from '@mui/material';
import { Visibility, VisibilityOff, School, CheckCircleOutline } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useSystemSettings } from '../components/DynamicThemeProvider';

const phrases = [
  'Conserva el histórico completo de tus estudiantes, incluso si los cursos son dados de baja en Moodle.',
  'Sincroniza y respalda automáticamente calificaciones, actividades y avatares en tiempo real.',
  'Toma el control con un gestor académico centralizado, dinámico y totalmente parametrizable.',
  'Respaldo persistente de fotos de perfil de los alumnos para un registro de desempeño inalterable.',
  'Monitorea el progreso con barras de avance en tiempo real y logs históricos de sincronización.'
];

const LoginPage: React.FC = () => {
  const [usernameOrEmail, setUsernameOrEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const { settings } = useSystemSettings();

  // Carousel phrase states
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [fadeState, setFadeState] = useState<'in' | 'out'>('in');

  useEffect(() => {
    const interval = setInterval(() => {
      setFadeState('out');
      setTimeout(() => {
        setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
        setFadeState('in');
      }, 500); // 500ms fade out transition
    }, 6000); // Rotate phrase every 6 seconds
    
    return () => clearInterval(interval);
  }, []);

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // In users view, email field accepts either local email or moodle_username
      await login({ email: usernameOrEmail, password });
      navigate(from, { replace: true });
    } catch (err: any) {
      console.error(err);
      const errorObj = err.response?.data?.error;
      let errMsg = 'Credenciales inválidas. Verifica tu usuario/correo y contraseña.';
      if (errorObj) {
        if (typeof errorObj === 'string') {
          errMsg = errorObj;
        } else if (typeof errorObj === 'object') {
          if (errorObj.message) {
            errMsg = errorObj.message;
            if (errorObj.details) {
              const details = errorObj.details;
              const detailMsgs = Object.keys(details)
                .map(key => `${key}: ${Array.isArray(details[key]) ? details[key].join(', ') : JSON.stringify(details[key])}`)
                .join(' | ');
              if (detailMsgs) {
                errMsg = `${errorObj.message} (${detailMsgs})`;
              }
            }
          }
        }
      }
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container sx={{ minHeight: '100vh', overflow: 'hidden' }}>
      
      {/* LEFT SIDE: Value Propositions (Animated Liquid Aurora Mesh) */}
      <Grid 
        item 
        xs={12} 
        md={6} 
        sx={{ 
          background: 'linear-gradient(135deg, #0A2540 0%, #7C4DFF 50%, #0070E0 100%)',
          backgroundSize: '400% 400%',
          animation: 'aurora-mesh 15s ease infinite',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: 6,
          color: '#FFFFFF',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Abstract floating glowing rings */}
        <Box sx={{ 
          position: 'absolute', 
          width: 500, 
          height: 500, 
          borderRadius: '50%', 
          background: 'radial-gradient(circle, rgba(124, 77, 255, 0.25) 0%, transparent 70%)',
          top: -150,
          left: -150,
          pointerEvents: 'none'
        }} />
        
        <Box sx={{ 
          position: 'absolute', 
          width: 600, 
          height: 600, 
          borderRadius: '50%', 
          background: 'radial-gradient(circle, rgba(0, 112, 224, 0.2) 0%, transparent 60%)',
          bottom: -150,
          right: -150,
          pointerEvents: 'none'
        }} />

        {/* Top Header */}
        <Box display="flex" alignItems="center" sx={{ zIndex: 2 }}>
          {settings.logo ? (
            <Avatar 
              src={settings.logo} 
              variant="rounded"
              sx={{ 
                width: 44, 
                height: 44, 
                mr: 2, 
                border: '1.5px solid rgba(255,255,255,0.2)', 
                backgroundColor: '#FFFFFF', 
                p: 0.5, 
                objectFit: 'contain',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }} 
            />
          ) : (
            <School sx={{ fontSize: 36, color: '#FFFFFF', mr: 2 }} />
          )}
          <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: 0.5 }}>
            {settings.institution_name}
          </Typography>
        </Box>

        {/* Dynamic Animated Carousel */}
        <Box sx={{ zIndex: 2, pr: 8, my: 'auto' }}>
          <Box display="flex" alignItems="center" mb={2}>
            <CheckCircleOutline sx={{ color: '#00D9A5', mr: 1, fontSize: 24 }} />
            <Typography variant="overline" color="rgba(255, 255, 255, 0.8)" fontWeight={700} sx={{ letterSpacing: 2 }}>
              Ventaja CreaSync
            </Typography>
          </Box>
          <Box 
            sx={{ 
              transition: 'opacity 0.5s ease-in-out, transform 0.5s ease-in-out',
              opacity: fadeState === 'in' ? 1 : 0,
              transform: fadeState === 'in' ? 'translateY(0px)' : 'translateY(-15px)',
              minHeight: 180
            }}
          >
            <Typography 
              variant="h3" 
              fontWeight={800} 
              lineHeight={1.3} 
              sx={{ color: '#FFFFFF', mb: 3, textShadow: '0 4px 12px rgba(10,37,64,0.15)' }}
            >
              {phrases[currentPhraseIndex]}
            </Typography>
          </Box>
        </Box>

        {/* Footer */}
        <Box display="flex" justifyContent="space-between" sx={{ zIndex: 2, color: 'rgba(255,255,255,0.5)' }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            © {new Date().getFullYear()} CreaSync.
          </Typography>
          <Typography variant="body2" sx={{ cursor: 'pointer', fontWeight: 500, '&:hover': { color: '#FFFFFF' } }}>
            Privacidad & Términos
          </Typography>
        </Box>
      </Grid>

      {/* RIGHT SIDE: Elegant Glassmorphism Login Form */}
      <Grid 
        item 
        xs={12} 
        md={6} 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center', 
          backgroundColor: '#F5F7FA',
          position: 'relative',
          p: { xs: 3, md: 8 }
        }}
      >
        {/* Soft background glow on mobile */}
        <Box sx={{ 
          position: 'absolute', 
          width: 300, 
          height: 300, 
          borderRadius: '50%', 
          background: 'radial-gradient(circle, rgba(124, 77, 255, 0.08) 0%, transparent 70%)',
          zIndex: 0,
          pointerEvents: 'none',
          display: { xs: 'block', md: 'none' }
        }} />

        <Box 
          sx={{ 
            maxWidth: 430, 
            width: '100%', 
            backgroundColor: 'rgba(255, 255, 255, 0.8)', 
            backdropFilter: 'blur(16px)',
            borderRadius: 5, 
            p: { xs: 4, sm: 5 },
            border: '1px solid rgba(226, 232, 240, 0.8)',
            boxShadow: '0px 24px 60px rgba(10, 37, 64, 0.06)',
            zIndex: 1
          }}
        >
          {/* Header Mobile / Brand Title */}
          <Box display="flex" flexDirection="column" alignItems="center" mb={4.5}>
            {settings.logo ? (
              <Avatar 
                src={settings.logo} 
                variant="rounded"
                sx={{ 
                  width: 64, 
                  height: 64, 
                  mb: 2, 
                  border: '1px solid #E2E8F0', 
                  backgroundColor: '#FFFFFF', 
                  p: 0.5, 
                  objectFit: 'contain',
                  boxShadow: '0 4px 10px rgba(0,0,0,0.02)'
                }} 
              />
            ) : (
              <School color="secondary" sx={{ fontSize: 56, mb: 1 }} />
            )}
            <Typography variant="h4" fontWeight={850} color="primary" sx={{ letterSpacing: -0.5 }} gutterBottom>
              Ingreso Académico
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ maxWidth: '85%' }}>
              Gestiona e integra tu Moodle con {settings.institution_name}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" variant="filled" sx={{ mb: 3.5, borderRadius: 3, fontWeight: 500 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              label="Usuario o Correo Electrónico"
              type="text"
              name="usernameOrEmail"
              fullWidth
              required
              value={usernameOrEmail}
              onChange={(e) => setUsernameOrEmail(e.target.value)}
              autoComplete="username"
              sx={{ 
                mb: 2.5,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  transition: 'all 0.2s',
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#7C4DFF',
                    boxShadow: '0 0 0 4px rgba(124, 77, 255, 0.1)'
                  }
                }
              }}
            />
            <TextField
              label="Contraseña"
              type={showPassword ? 'text' : 'password'}
              name="password"
              fullWidth
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              sx={{ 
                mb: 4.5,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  transition: 'all 0.2s',
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#7C4DFF',
                    boxShadow: '0 0 0 4px rgba(124, 77, 255, 0.1)'
                  }
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              fullWidth
              size="large"
              sx={{ 
                py: 1.8, 
                borderRadius: 30,
                fontSize: '1rem',
                fontWeight: 700,
                background: 'linear-gradient(135deg, #7C4DFF 0%, #0070E0 100%)',
                boxShadow: '0 4px 16px rgba(124, 77, 255, 0.25)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #6c3ce6 0%, #0060c0 100%)',
                  boxShadow: '0 6px 20px rgba(124, 77, 255, 0.35)'
                }
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Iniciar Sesión'}
            </Button>
          </form>

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              ¿Problemas de ingreso?{' '}
              <Button size="small" sx={{ textTransform: 'none', fontWeight: 700, color: '#0070E0' }}>
                Contactar soporte
              </Button>
            </Typography>
          </Box>
        </Box>
      </Grid>
      
    </Grid>
  );
};

export default LoginPage;
